import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import './graph.css';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { EditPersonModal } from './PersonModal/EditPersonModal';
import { EditEdgeModal } from './EdgeModal/EditEdgeModal';
import { CreateExistingEdgeModal } from './EdgeModal/CreateExistingEdgeModal';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SnackbarContent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CreatePersonModal } from './CreatePersonModal/CreatePersonModal';
import { useTheme } from '@emotion/react';
import useIsTouchscreen, { drawNodePhoto, getColor } from './utils';
import Snackbar from '@mui/material/Snackbar';
import { useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InsightsIcon from '@mui/icons-material/Insights';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import StopIcon from '@mui/icons-material/Stop';
import { useEditorSession } from '../Auth/GoogleEditorGate';
import { forceCollide } from 'd3-force-3d';
import { getNonOriginalGraphEdges, getOriginalGraphEdges } from '../graphEdges';
import { analyzeGraph, shortestPath, simulateRemoval, sortEdgesByDate } from './graphAnalytics';
import {
  GRAPH_CENTER_STRENGTH,
  GRAPH_CHARGE_STRENGTH,
  GRAPH_COLLISION_ITERATIONS,
  GRAPH_COLLISION_RADIUS,
  GRAPH_COLLISION_STRENGTH,
  GRAPH_LINK_DISTANCE,
  GRAPH_NODE_SIZE,
} from '../graphLayout';
import { getEditGraphPath, getGraphNameParam, getReadGraphPath } from '../graphRoutes';
import { getPersonPhotoImage, preloadPersonPhotoImages, prunePersonPhotoCache } from '../graphPhotos';
import { getEdgeCategoryColor } from '../edgeCategories';
import { GRAPH_TYPE_OPTIONS, getGraphTypeLabel, normalizeGraphType } from '../graphTypes';

const PEOPLE_QUERY = gql`
  query People($graphName: String) {
    graph(graphName: $graphName) {
      _id
      name
      slug
      type
      isDefault
      focusPersonId
    }
    graphs {
      _id
      name
      slug
      type
      isDefault
    }
    people(graphName: $graphName) {
      _id
      graphId
      name
      imageURL
      funFact
      birthDate
      deathDate
      locationX
      locationY
    }
    edges(graphName: $graphName) {
      _id
      graphId
      person1
      person2
      notes
      category
      dateMet
      images {
        imageURL
        caption
      }
      interviewQuestions {
            question
            answer
            personId
      }
    }
    graphSuggestions(graphName: $graphName, status: "pending") {
      _id
      status
      kind
      targetPersonId
      suggestedPersonName
      suggestedPersonImageURL
      connectToPersonId
      dateMet
      category
      notes
      storyTitle
      storyText
      storyDate
      taggedPersonIds
      submitterName
      submitterEmail
      submitterNote
      approvedStoryId
      createdOn
    }
  }
`;

const ADD_GRAPH_MUTATION = gql`
  mutation AddGraph($name: String!, $slug: String, $type: String) {
    addGraph(name: $name, slug: $slug, type: $type) {
      _id
      name
      slug
      type
      isDefault
    }
  }
`;

const RENAME_GRAPH_MUTATION = gql`
  mutation RenameGraph($graphId: ID, $graphName: String, $name: String!, $slug: String, $type: String) {
    renameGraph(graphId: $graphId, graphName: $graphName, name: $name, slug: $slug, type: $type) {
      _id
      name
      slug
      type
      isDefault
    }
  }
`;

const DELETE_GRAPH_MUTATION = gql`
  mutation DeleteGraph($graphName: String) {
    deleteGraph(graphName: $graphName)
  }
`;

const IMPORT_GRAPH_NEIGHBORHOOD_MUTATION = gql`
  mutation ImportGraphNeighborhood($sourceGraphName: String, $targetGraphName: String, $startPersonId: ID!, $degrees: Int!) {
    importGraphNeighborhood(sourceGraphName: $sourceGraphName, targetGraphName: $targetGraphName, startPersonId: $startPersonId, degrees: $degrees) {
      peopleImported
      peopleReused
      edgesImported
      edgesReused
    }
  }
`;

const normalizeImportDegrees = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
  return Math.floor(numericValue);
};

const normalizeGraphUrlName = (value = '') => (
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

function stableCoordinate(id, axis) {
  const input = `${id || ''}-${axis}`;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return (Math.abs(hash) % 320) - 160;
}

const SOURCE_PEOPLE_QUERY = gql`
  query SourcePeople($graphName: String) {
    people(graphName: $graphName) {
      _id
      name
      imageURL
      funFact
    }
  }
`;

const DELETE_PERSON_MUTATION = gql`
  mutation DeletePerson($id: ID!) {
    deletePerson(id: $id)
  }
`;

const DELETE_EDGE_MUTATION = gql`
  mutation DeleteEdge($edgeId: ID!) {
    deleteEdge(edgeId: $edgeId)
  }
`;

const DELETE_PEOPLE_MUTATION = gql`
  mutation DeletePeople($ids: [ID!]!) {
    deletePeople(ids: $ids)
  }
`;

const DELETE_EDGES_MUTATION = gql`
  mutation DeleteEdges($edgeIds: [ID!]!) {
    deleteEdges(edgeIds: $edgeIds)
  }
`;

const UPDATE_LOCATION_MUTATION = gql`
  mutation UpdateLocation($nodesToUpdate: [NodeInput], $graphName: String, $focusPersonId: ID) {
    updateLocation(nodesToUpdate: $nodesToUpdate, graphName: $graphName, focusPersonId: $focusPersonId)
  }
`;

const APPROVE_GRAPH_SUGGESTION_MUTATION = gql`
  mutation ApproveGraphSuggestion($id: ID!) {
    approveGraphSuggestion(id: $id) {
      suggestion {
        _id
        status
      }
      person {
        _id
      }
      edge {
        _id
      }
    }
  }
`;

const REJECT_GRAPH_SUGGESTION_MUTATION = gql`
  mutation RejectGraphSuggestion($id: ID!) {
    rejectGraphSuggestion(id: $id) {
      _id
      status
    }
  }
`;

export default function Graph() {
  const params = useParams();
  const navigate = useNavigate();
  const { user, signOut } = useEditorSession();
  const graphName = getGraphNameParam(params);
  const { data: graphData, loading, error, refetch: refetchGraph } = useQuery(PEOPLE_QUERY, {
    variables: { graphName },
  });
  const [addGraph, { loading: isAddingGraph }] = useMutation(ADD_GRAPH_MUTATION);
  const [renameGraph, { loading: isRenamingGraph }] = useMutation(RENAME_GRAPH_MUTATION);
  const [deleteGraphMutation, { loading: isDeletingGraph }] = useMutation(DELETE_GRAPH_MUTATION);
  const [importGraphNeighborhood, { loading: isImportingGraph }] = useMutation(IMPORT_GRAPH_NEIGHBORHOOD_MUTATION);
  const [loadSourcePeople, { data: sourcePeopleData, loading: isLoadingSourcePeople }] = useLazyQuery(SOURCE_PEOPLE_QUERY);
  const [deletePerson] = useMutation(DELETE_PERSON_MUTATION);
  const [deleteEdge] = useMutation(DELETE_EDGE_MUTATION);
  const [deletePeople] = useMutation(DELETE_PEOPLE_MUTATION);
  const [deleteEdges] = useMutation(DELETE_EDGES_MUTATION);
  const [updateLocation, { loading: isSavingLayout }] = useMutation(UPDATE_LOCATION_MUTATION);
  const [approveGraphSuggestion, { loading: isApprovingSuggestion }] = useMutation(APPROVE_GRAPH_SUGGESTION_MUTATION);
  const [rejectGraphSuggestion, { loading: isRejectingSuggestion }] = useMutation(REJECT_GRAPH_SUGGESTION_MUTATION);
  const [layoutMessage, setLayoutMessage] = useState('');

  const [data, setData] = useState({ nodes: [], links: [] });
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCreateEdgeOpen, setIsCreateEdgeOpen] = useState(false);
  const [isDataPanelOpen, setIsDataPanelOpen] = useState(false);
  const [isGraphPanelOpen, setIsGraphPanelOpen] = useState(false);
  const [isInsightsPanelOpen, setIsInsightsPanelOpen] = useState(false);
  const [isApprovalsPanelOpen, setIsApprovalsPanelOpen] = useState(false);
  const [dataPanelTab, setDataPanelTab] = useState('people');
  const [savedDataSearch, setSavedDataSearch] = useState('');
  const [graphNameDraft, setGraphNameDraft] = useState('');
  const [graphUrlNameDraft, setGraphUrlNameDraft] = useState('');
  const [graphTypeDraft, setGraphTypeDraft] = useState('social');
  const [sourceGraphName, setSourceGraphName] = useState('');
  const [sourceStartPersonId, setSourceStartPersonId] = useState('');
  const [importDegrees, setImportDegrees] = useState(1);
  const [pathStartPersonId, setPathStartPersonId] = useState('');
  const [pathEndPersonId, setPathEndPersonId] = useState('');
  const [whatIfPersonId, setWhatIfPersonId] = useState('');
  const [isTimelineActive, setIsTimelineActive] = useState(false);
  const [isTimelinePlaying, setIsTimelinePlaying] = useState(false);
  const [timelineStep, setTimelineStep] = useState(0);
  const [selectedSavedPeople, setSelectedSavedPeople] = useState(() => new Set());
  const [selectedSavedEdges, setSelectedSavedEdges] = useState(() => new Set());
  const graphRef = useRef();
  const refreshFrameRef = useRef(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);

  const theme = useTheme();

  const isTouchDevice = useIsTouchscreen();

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    if (!isTouchDevice) {
      setIsModalOpen(true);
    }
  };

  const scheduleGraphRefresh = useCallback(() => {
    if (refreshFrameRef.current) return;
    refreshFrameRef.current = window.requestAnimationFrame(() => {
      refreshFrameRef.current = null;
      graphRef.current?.refresh?.();
    });
  }, []);

  const handleEdgeClick = (edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    if (!isTouchDevice) {
      setIsEdgeModalOpen(true);
    }
  };

  const idToPerson = useMemo(() => {
    if (!graphData) return {};
    const { people } = graphData;
    return people.reduce((acc, person) => {
      acc[person._id] = person;
      return acc;
    }, {});
  }, [graphData]);

  const getPersonByEndpoint = useCallback((endpoint) => {
    const id = typeof endpoint === 'object' ? endpoint?.id : endpoint;
    return idToPerson[id];
  }, [idToPerson]);

  const getEdgePersonLabel = useCallback((edge) => {
    const person1 = idToPerson[edge.person1] || getPersonByEndpoint(edge.source);
    const person2 = idToPerson[edge.person2] || getPersonByEndpoint(edge.target);
    return `${person1?.name || 'Person'} + ${person2?.name || 'Person'}`;
  }, [getPersonByEndpoint, idToPerson]);

  const formatDate = (value) => {
    if (!value) return 'No date';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'No date' : date.toLocaleDateString();
  };

  const selectedLabel = selectedNode
    ? selectedNode.name
    : selectedEdge
      ? getEdgePersonLabel(selectedEdge)
      : '';

  const editSelected = () => {
    if (selectedNode) setIsModalOpen(true);
    if (selectedEdge) setIsEdgeModalOpen(true);
  };

  const deleteSelected = async () => {
    if (selectedNode) {
      const confirmed = window.confirm(`Delete ${selectedNode.name} and every story connected to them?`);
      if (!confirmed) return;
      await deletePerson({ variables: { id: selectedNode.id } });
      setSelectedNode(null);
    }

    if (selectedEdge) {
      const confirmed = window.confirm('Delete the selected story?');
      if (!confirmed) return;
      await deleteEdge({ variables: { edgeId: selectedEdge._id } });
      setSelectedEdge(null);
    }

    await refetchGraph();
  };

  const toggleSavedPerson = (id) => {
    setSelectedSavedPeople((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSavedEdge = (id) => {
    setSelectedSavedEdges((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectVisiblePeople = (people) => {
    setSelectedSavedPeople((current) => {
      const next = new Set(current);
      people.forEach((person) => next.add(person._id));
      return next;
    });
  };

  const selectVisibleEdges = (edges) => {
    setSelectedSavedEdges((current) => {
      const next = new Set(current);
      edges.forEach((edge) => next.add(edge._id));
      return next;
    });
  };

  const clearSavedSelection = () => {
    setSelectedSavedPeople(new Set());
    setSelectedSavedEdges(new Set());
  };

  const getPersonName = useCallback((personId) => {
    return idToPerson[personId]?.name || 'Unknown person';
  }, [idToPerson]);

  const getFriendshipLabel = useCallback((edge) => {
    if (!edge) return 'No dated friendship yet';
    return `${getPersonName(edge.person1)} + ${getPersonName(edge.person2)} (${formatDate(edge.dateMet)})`;
  }, [getPersonName]);

  const analyticsEdges = useMemo(() => graphData?.edges || [], [graphData]);
  const analyticsPeople = useMemo(() => graphData?.people || [], [graphData]);
  const graphAnalytics = useMemo(() => (
    analyzeGraph(analyticsPeople, analyticsEdges)
  ), [analyticsEdges, analyticsPeople]);
  const pendingSuggestions = graphData?.graphSuggestions || [];
  const timelineEdges = useMemo(() => sortEdgesByDate(analyticsEdges), [analyticsEdges]);
  const smallWorldEdges = useMemo(() => (
    getNonOriginalGraphEdges(analyticsEdges)
      .sort((a, b) => new Date(b.dateMet || 0).getTime() - new Date(a.dateMet || 0).getTime())
  ), [analyticsEdges]);
  const pathPersonIds = useMemo(() => (
    shortestPath(analyticsPeople, analyticsEdges, pathStartPersonId, pathEndPersonId)
  ), [analyticsEdges, analyticsPeople, pathEndPersonId, pathStartPersonId]);
  const whatIfResult = useMemo(() => (
    simulateRemoval(analyticsPeople, analyticsEdges, whatIfPersonId)
  ), [analyticsEdges, analyticsPeople, whatIfPersonId]);

  const startTimeline = () => {
    if (!timelineEdges.length) return;
    setSelectedNode(null);
    setSelectedEdge(null);
    setIsTimelineActive(true);
    setTimelineStep(0);
    setIsTimelinePlaying(true);
  };

  const stopTimeline = () => {
    setIsTimelinePlaying(false);
    setIsTimelineActive(false);
    setTimelineStep(0);
  };

  const zoomToFit = () => {
    graphRef.current?.zoomToFit?.(500, 60);
  };

  const toggleSidePanel = (panelName) => {
    setIsDataPanelOpen((open) => panelName === 'data' ? !open : false);
    setIsGraphPanelOpen((open) => panelName === 'graph' ? !open : false);
    setIsInsightsPanelOpen((open) => panelName === 'insights' ? !open : false);
    setIsApprovalsPanelOpen((open) => panelName === 'approvals' ? !open : false);
  };

  const openGraph = (name) => {
    navigate(getEditGraphPath(name));
  };

  const copyReadGraphLink = async () => {
    const activeUrlName = graphData?.graph?.slug || graphData?.graph?.name || graphName;
    const readPath = getReadGraphPath(activeUrlName);
    const url = `${window.location.origin}${readPath}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setLayoutMessage(`Copied read-only link: ${readPath}`);
    } catch (copyError) {
      setLayoutMessage(`Read-only link: ${url}`);
    }
  };

  const createGraph = async () => {
    const name = window.prompt('Name this map');
    if (!name?.trim()) return;
    const requestedType = window.prompt('Map type: social, family, or wedding', 'social');
    const type = normalizeGraphType(requestedType?.trim());

    try {
      const result = await addGraph({ variables: { name: name.trim(), type } });
      const createdName = result.data?.addGraph?.name;
      await refetchGraph();
      if (createdName) {
        openGraph(createdName);
        setIsPanelOpen(true);
      }
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not create map.');
    }
  };

  const saveGraphName = async () => {
    const nextName = graphNameDraft.trim();
    const nextSlug = normalizeGraphUrlName(graphUrlNameDraft || nextName);
    const nextType = normalizeGraphType(graphTypeDraft);
    const currentType = normalizeGraphType(graphData?.graph?.type);
    const currentSlug = graphData?.graph?.slug || '';
    if (!nextName || !nextSlug || (nextName === graphData?.graph?.name && nextSlug === currentSlug && nextType === currentType)) return;

    try {
      const result = await renameGraph({
        variables: {
          graphId: graphData?.graph?._id,
          graphName: graphData?.graph?.name || graphName,
          name: nextName,
          slug: nextSlug,
          type: nextType,
        },
      });
      const renamedName = result.data?.renameGraph?.name || nextName;
      const renamedSlug = result.data?.renameGraph?.slug || nextSlug;
      setLayoutMessage(`Saved ${renamedName} as a ${getGraphTypeLabel(nextType)}.`);
      navigate(getEditGraphPath(renamedSlug), { replace: true });
      await refetchGraph();
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not rename map.');
    }
  };

  const deleteCurrentGraph = async () => {
    const currentName = graphData?.graph?.name;
    if (!currentName || graphData?.graph?.isDefault) return;
    const confirmed = window.confirm(`Delete "${currentName}" and every person/story inside it? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteGraphMutation({ variables: { graphName: currentName } });
      setLayoutMessage(`Deleted ${currentName}.`);
      navigate('/edit', { replace: true });
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not delete map.');
    }
  };

  const selectImportSourceGraph = (name) => {
    setSourceGraphName(name);
    setSourceStartPersonId('');
    if (name) {
      loadSourcePeople({ variables: { graphName: name } });
    }
  };

  const importFromGraph = async () => {
    if (!sourceGraphName || !sourceStartPersonId || !graphData?.graph?.name) return;
    try {
      const result = await importGraphNeighborhood({
        variables: {
          sourceGraphName,
          targetGraphName: graphData.graph.name,
          startPersonId: sourceStartPersonId,
          degrees: normalizeImportDegrees(importDegrees),
        },
      });
      const imported = result.data?.importGraphNeighborhood;
      setLayoutMessage(
        `Imported ${imported?.peopleImported || 0} people and ${imported?.edgesImported || 0} stories. Reused ${imported?.peopleReused || 0} people and ${imported?.edgesReused || 0} stories.`
      );
      await refetchGraph();
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not import map data.');
    }
  };

  const approveSuggestion = async (suggestion) => {
    const isPhotoUpdate = suggestion.kind === 'photo_update';
    const isPersonStory = suggestion.kind === 'person_story';
    try {
      await approveGraphSuggestion({ variables: { id: suggestion._id } });
      setLayoutMessage(
        isPhotoUpdate
          ? `Approved new photo for ${getPersonName(suggestion.targetPersonId)}.`
          : isPersonStory
            ? `Approved story for ${getPersonName(suggestion.targetPersonId)}.`
            : `Approved ${suggestion.suggestedPersonName}.`
      );
      await refetchGraph();
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not approve this suggestion.');
    }
  };

  const rejectSuggestion = async (suggestion) => {
    const isPhotoUpdate = suggestion.kind === 'photo_update';
    const isPersonStory = suggestion.kind === 'person_story';
    const confirmed = window.confirm(isPhotoUpdate
      ? `Reject new photo for ${getPersonName(suggestion.targetPersonId)}?`
      : isPersonStory
        ? `Reject story for ${getPersonName(suggestion.targetPersonId)}?`
      : `Reject ${suggestion.suggestedPersonName}?`);
    if (!confirmed) return;
    try {
      await rejectGraphSuggestion({ variables: { id: suggestion._id } });
      setLayoutMessage(
        isPhotoUpdate
          ? `Rejected new photo for ${getPersonName(suggestion.targetPersonId)}.`
          : isPersonStory
            ? `Rejected story for ${getPersonName(suggestion.targetPersonId)}.`
            : `Rejected ${suggestion.suggestedPersonName}.`
      );
      await refetchGraph();
    } catch (mutationError) {
      window.alert(mutationError.message || 'Could not reject this suggestion.');
    }
  };

  const applyGraphPhysics = useCallback((reheat = false) => {
    if (!graphRef.current) return;
    graphRef.current.d3Force?.('charge')?.strength?.(GRAPH_CHARGE_STRENGTH);
    graphRef.current.d3Force?.('link')?.distance?.(GRAPH_LINK_DISTANCE);
    graphRef.current.d3Force?.('center')?.strength?.(GRAPH_CENTER_STRENGTH);
    graphRef.current.d3Force?.(
      'collide',
      forceCollide(GRAPH_COLLISION_RADIUS)
        .strength(GRAPH_COLLISION_STRENGTH)
        .iterations(GRAPH_COLLISION_ITERATIONS)
    );
    if (reheat) graphRef.current.d3ReheatSimulation?.();
  }, []);

  const saveGraphLayout = async () => {
    const nodesToUpdate = (data.nodes || [])
      .filter((node) => node.id && Number.isFinite(node.x) && Number.isFinite(node.y))
      .map((node) => ({
        id: node.id,
        locationX: node.x,
        locationY: node.y,
      }));

    if (!nodesToUpdate.length) return;
    await updateLocation({
      variables: {
        nodesToUpdate,
        graphName,
        focusPersonId: graphData?.people?.[0]?._id,
      },
    });
    setLayoutMessage(`Saved ${nodesToUpdate.length} map positions.`);
    await refetchGraph();
  };

  const focusPerson = (person) => {
    const node = data.nodes.find((item) => item.id === person._id);
    setSelectedNode(node || {
      id: person._id,
      name: person.name,
      image: person.imageURL,
      funFact: person.funFact,
    });
    setSelectedEdge(null);
    if (node && typeof node.x === 'number' && typeof node.y === 'number') {
      graphRef.current?.centerAt?.(node.x, node.y, 500);
      graphRef.current?.zoom?.(2.2, 500);
    }
  };

  const focusEdge = (edge) => {
    const link = data.links.find((item) => item._id === edge._id) || {
      ...edge,
      id: edge._id,
      source: edge.person1,
      target: edge.person2,
      note: edge.notes,
    };
    setSelectedEdge(link);
    setSelectedNode(null);
    const source = typeof link.source === 'object' ? link.source : data.nodes.find((node) => node.id === link.source);
    const target = typeof link.target === 'object' ? link.target : data.nodes.find((node) => node.id === link.target);
    if (source && target && typeof source.x === 'number' && typeof target.x === 'number') {
      graphRef.current?.centerAt?.((source.x + target.x) / 2, (source.y + target.y) / 2, 500);
      graphRef.current?.zoom?.(2, 500);
    }
  };

  const originalEdgeIds = useMemo(() => {
    return new Set(getOriginalGraphEdges(graphData?.edges || []).map((edge) => edge._id));
  }, [graphData]);

  const bulkDeleteSavedData = async () => {
    const personIds = Array.from(selectedSavedPeople);
    const edgeIds = Array.from(selectedSavedEdges);
    if (!personIds.length && !edgeIds.length) return;

    const personIdSet = new Set(personIds);
    const remainingEdgeIds = edgeIds.filter((edgeId) => {
      const edge = graphData?.edges?.find((item) => item._id === edgeId);
      return edge && !personIdSet.has(edge.person1) && !personIdSet.has(edge.person2);
    });
    const connectedEdgesCount = (graphData?.edges || []).filter((edge) => personIdSet.has(edge.person1) || personIdSet.has(edge.person2)).length;
    const pieces = [];
    if (personIds.length) pieces.push(`${personIds.length} ${personIds.length === 1 ? 'person' : 'people'}`);
    if (remainingEdgeIds.length) pieces.push(`${remainingEdgeIds.length} ${remainingEdgeIds.length === 1 ? 'story' : 'stories'}`);
    if (connectedEdgesCount) pieces.push(`${connectedEdgesCount} connected ${connectedEdgesCount === 1 ? 'story' : 'stories'}`);
    const confirmed = window.confirm(`Delete ${pieces.join(', ')}? This cannot be undone.`);
    if (!confirmed) return;

    if (personIds.length) {
      await deletePeople({ variables: { ids: personIds } });
    }
    if (remainingEdgeIds.length) {
      await deleteEdges({ variables: { edgeIds: remainingEdgeIds } });
    }

    clearSavedSelection();
    setSelectedNode(null);
    setSelectedEdge(null);
    await refetchGraph();
  };

  useEffect(() => {
    if (!graphData) return;

    const { people, edges } = graphData;
    const nodes = people.map((user) => {
      const hasSavedLocation = Number.isFinite(user.locationX) && Number.isFinite(user.locationY);
      return {
        ...user,
        id: user._id,
        name: user.name,
        image: user.imageURL || `https://robohash.org/${user.name}`,
        funFact: user.funFact,
        x: hasSavedLocation ? user.locationX : stableCoordinate(user._id, 'x'),
        y: hasSavedLocation ? user.locationY : stableCoordinate(user._id, 'y'),
      };
    });

    const links = getOriginalGraphEdges(edges).map((edge) => ({
        ...edge,
        source: edge.person1,
        target: edge.person2,
        note: edge.notes,
        dateMet: edge.dateMet,
        id: edge._id,
    }));

    setData({ nodes, links });
  }, [graphData]);

  useEffect(() => {
    applyGraphPhysics(true);
  }, [applyGraphPhysics, data.nodes.length, data.links.length]);

  useEffect(() => {
    if (!graphData) return;

    if (selectedNode && !graphData.people.some((person) => person._id === selectedNode.id)) {
      setSelectedNode(null);
    }

    if (selectedEdge && !graphData.edges.some((edge) => edge._id === selectedEdge._id)) {
      setSelectedEdge(null);
    }

    setSelectedSavedPeople((current) => new Set([...current].filter((id) => graphData.people.some((person) => person._id === id))));
    setSelectedSavedEdges((current) => new Set([...current].filter((id) => graphData.edges.some((edge) => edge._id === id))));
  }, [graphData, selectedEdge, selectedNode]);

  useEffect(() => {
    setGraphNameDraft(graphData?.graph?.name || '');
    setGraphUrlNameDraft(graphData?.graph?.slug || '');
    setGraphTypeDraft(normalizeGraphType(graphData?.graph?.type));
  }, [graphData?.graph?.name, graphData?.graph?.slug, graphData?.graph?.type]);

  useEffect(() => {
    prunePersonPhotoCache(graphData?.people || []);
    preloadPersonPhotoImages(graphData?.people || [], scheduleGraphRefresh);
  }, [graphData?.people, scheduleGraphRefresh]);

  useEffect(() => () => {
    if (refreshFrameRef.current) {
      window.cancelAnimationFrame(refreshFrameRef.current);
    }
  }, []);

  useEffect(() => {
    if (!analyticsPeople.length) {
      setPathStartPersonId('');
      setPathEndPersonId('');
      setWhatIfPersonId('');
      return;
    }

    setPathStartPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : analyticsPeople[0]._id);
    setPathEndPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : analyticsPeople[1]?._id || analyticsPeople[0]._id);
    setWhatIfPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : analyticsPeople[0]._id);
  }, [analyticsPeople]);

  useEffect(() => {
    if (!isTimelinePlaying) return undefined;
    if (!timelineEdges.length) {
      stopTimeline();
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimelineStep((current) => {
        if (current >= timelineEdges.length) {
          setIsTimelinePlaying(false);
          return current;
        }
        return current + 1;
      });
    }, 900);

    return () => window.clearInterval(intervalId);
  }, [isTimelinePlaying, timelineEdges.length]);

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  const selectedEdgePerson1 = selectedEdge ? getPersonByEndpoint(selectedEdge.source) : null;
  const selectedEdgePerson2 = selectedEdge ? getPersonByEndpoint(selectedEdge.target) : null;
  const normalizedSavedSearch = savedDataSearch.trim().toLowerCase();
  const visiblePeople = useMemo(() => {
    const people = graphData?.people || [];
    if (!normalizedSavedSearch) return people;
    return people.filter((person) => [person.name, person.funFact].some((value) => value?.toLowerCase().includes(normalizedSavedSearch)));
  }, [graphData, normalizedSavedSearch]);
  const visibleEdges = useMemo(() => {
    const edges = graphData?.edges || [];
    if (!normalizedSavedSearch) return edges;
    return edges.filter((edge) => [
      getEdgePersonLabel(edge),
      edge.notes,
      formatDate(edge.dateMet),
    ].some((value) => value?.toLowerCase().includes(normalizedSavedSearch)));
  }, [getEdgePersonLabel, graphData, normalizedSavedSearch]);
  const bulkDeleteCount = selectedSavedPeople.size + selectedSavedEdges.size;
  const graphOptions = graphData?.graphs || (graphData?.graph ? [graphData.graph] : []);
  const activeGraphName = graphData?.graph?.name || '';
  const activeGraphRouteName = graphData?.graph?.slug || activeGraphName;
  const activeGraphType = normalizeGraphType(graphData?.graph?.type);
  const sourceGraphOptions = graphOptions.filter((graph) => graph._id !== graphData?.graph?._id);
  const sourcePeople = sourcePeopleData?.people || [];
  const timelineEdgeCount = Math.min(timelineStep, timelineEdges.length);
  const currentTimelineEdge = timelineEdges[Math.max(0, timelineEdgeCount - 1)] || timelineEdges[0];
  const displayedData = useMemo(() => {
    if (!isTimelineActive || !timelineEdges.length) return data;
    const visibleEdges = timelineEdges.slice(0, timelineEdgeCount);
    const visibleIds = new Set();

    visibleEdges.forEach((edge) => {
      visibleIds.add(edge.person1);
      visibleIds.add(edge.person2);
    });

    if (!visibleIds.size && timelineEdges[0]) {
      visibleIds.add(timelineEdges[0].person1);
    }

    return {
      nodes: data.nodes.filter((node) => visibleIds.has(node.id)),
      links: visibleEdges.map((edge) => ({
        ...edge,
        source: edge.person1,
        target: edge.person2,
        note: edge.notes,
        dateMet: edge.dateMet,
        id: edge._id,
      })),
    };
  }, [data, isTimelineActive, timelineEdgeCount, timelineEdges]);

  return (
    <div key="bottom">
      <div key="bottom" className="graph-container">
        <Paper className="graph-edit-toolbar" elevation={8}>
          <Stack direction="row" spacing={1} alignItems="center" className="graph-edit-toolbar-row">
            <Box className="graph-edit-toolbar-group graph-edit-toolbar-graph">
              <FormControl size="small" className="graph-graph-select">
                <Select
                  value={activeGraphRouteName}
                  onChange={(event) => openGraph(event.target.value)}
                  displayEmpty
                  disabled={loading}
                >
                  {!graphOptions.length && (
                    <MenuItem disabled value="">
                      Loading map...
                    </MenuItem>
                  )}
                  {graphOptions.map((graph) => (
                    <MenuItem key={graph._id || graph.name} value={graph.slug || graph.name}>
                      {graph.name}{graph.isDefault ? ' (Default)' : ''} - {getGraphTypeLabel(graph.type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={createGraph}
                className="graph-new-graph-action"
                disabled={isAddingGraph}
              >
                New Map
              </Button>
            </Box>
            <Box className="graph-edit-toolbar-group graph-edit-toolbar-actions">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={togglePanel}
                className="graph-primary-action"
              >
                Add Person
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddLinkIcon />}
                onClick={() => setIsCreateEdgeOpen(true)}
                className="graph-secondary-action"
                disabled={(graphData?.people?.length || 0) < 2}
              >
                Add Story
              </Button>
              <Tooltip title="Fit map">
                <IconButton color="primary" onClick={zoomToFit} className="graph-icon-action">
                  <CenterFocusStrongIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={saveGraphLayout}
                className="graph-secondary-action"
                disabled={isSavingLayout || !data.nodes.length}
              >
                {isSavingLayout ? 'Saving...' : 'Save Layout'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyReadGraphLink}
                className="graph-secondary-action"
                disabled={loading || !graphData?.graph?.name}
              >
                Copy Link
              </Button>
            </Box>
            <Chip
              color={selectedNode || selectedEdge ? 'primary' : 'default'}
              label={selectedNode || selectedEdge ? selectedLabel : loading ? 'Loading map...' : `${graphData?.graph?.name || 'Map'} • ${getGraphTypeLabel(activeGraphType)} • ${data.nodes.length} people • ${graphData?.edges?.length || 0} saved stories`}
              className="graph-status-chip"
            />
            <Tooltip title={`Sign out${user?.email ? ` ${user.email}` : ''}`}>
              <IconButton
                color="primary"
                onClick={signOut}
                className="graph-icon-action graph-signout-action"
                aria-label="Sign out"
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        <Paper className="graph-edit-nav" elevation={10} aria-label="Edit navigation">
          <button
            type="button"
            className={`graph-edit-nav-item${isDataPanelOpen ? ' graph-edit-nav-item--active' : ''}`}
            onClick={() => toggleSidePanel('data')}
          >
            <ManageSearchIcon fontSize="small" />
            <span>Data</span>
          </button>
          <button
            type="button"
            className={`graph-edit-nav-item${isApprovalsPanelOpen ? ' graph-edit-nav-item--active' : ''}`}
            onClick={() => toggleSidePanel('approvals')}
          >
            <ManageSearchIcon fontSize="small" />
            <span>Approvals</span>
            {pendingSuggestions.length > 0 && <strong>{pendingSuggestions.length}</strong>}
          </button>
          <button
            type="button"
            className={`graph-edit-nav-item${isGraphPanelOpen ? ' graph-edit-nav-item--active' : ''}`}
            onClick={() => toggleSidePanel('graph')}
          >
            <EditIcon fontSize="small" />
            <span>Tools</span>
          </button>
          <button
            type="button"
            className={`graph-edit-nav-item${isInsightsPanelOpen ? ' graph-edit-nav-item--active' : ''}`}
            onClick={() => toggleSidePanel('insights')}
          >
            <InsightsIcon fontSize="small" />
            <span>Insights</span>
          </button>
        </Paper>

        {(selectedNode || selectedEdge) && (
          <Paper className="graph-selection-panel" elevation={10}>
            <Box className="graph-selection-copy">
              <Typography variant="overline" color="text.secondary">
                Selected {selectedNode ? 'person' : 'story'}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {selectedLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNode
                  ? selectedNode.funFact || 'Edit their profile, photo, or delete them from the map.'
                  : selectedEdge.note || 'Edit or delete this relationship story.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} className="graph-selection-actions">
              <Button variant="contained" startIcon={<EditIcon />} onClick={editSelected}>
                Edit
              </Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={deleteSelected}>
                Delete
              </Button>
              <Button variant="text" onClick={() => { setSelectedNode(null); setSelectedEdge(null); }}>
                Clear
              </Button>
            </Stack>
          </Paper>
        )}

        <Paper className={`graph-approvals-panel${isApprovalsPanelOpen ? ' graph-approvals-panel--open' : ''}`} elevation={12}>
          <Stack spacing={1.5} className="graph-approvals-panel-content">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Public submissions
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  Pending approvals
                </Typography>
              </Box>
              <IconButton aria-label="Close approvals" onClick={() => setIsApprovalsPanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Review suggestions before they become real people and map stories.
            </Typography>

            <Divider />

            <Box className="graph-approval-list">
              {pendingSuggestions.map((suggestion) => {
                const isPhotoUpdate = suggestion.kind === 'photo_update';
                const isPersonStory = suggestion.kind === 'person_story';
                const displayName = isPhotoUpdate || isPersonStory
                  ? getPersonName(suggestion.targetPersonId)
                  : suggestion.suggestedPersonName;
                const taggedPeople = (suggestion.taggedPersonIds || []).map(getPersonName).filter(Boolean);

                return (
                  <Paper key={suggestion._id} className="graph-approval-card" elevation={0}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box className="graph-approval-avatar">
                        {suggestion.suggestedPersonImageURL ? (
                          <img src={suggestion.suggestedPersonImageURL} alt="" />
                        ) : (
                          <span>{(displayName || '?').charAt(0)}</span>
                        )}
                      </Box>
                      <Box minWidth={0}>
                        <Typography variant="subtitle1" fontWeight={900} noWrap>
                          {displayName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {isPhotoUpdate
                            ? 'new profile photo'
                            : isPersonStory
                              ? 'new personal story'
                            : `connects to ${getPersonName(suggestion.connectToPersonId)}`}
                        </Typography>
                      </Box>
                    </Stack>
                    <Box className="graph-approval-meta">
                      <span>{isPhotoUpdate ? 'photo update' : isPersonStory ? 'person story' : formatDate(suggestion.dateMet)}</span>
                      {isPersonStory && suggestion.storyDate && <span>{formatDate(suggestion.storyDate)}</span>}
                      {!isPhotoUpdate && !isPersonStory && <span>{suggestion.category || 'friendship'}</span>}
                      {isPersonStory && taggedPeople.length > 0 && <span>with {taggedPeople.join(', ')}</span>}
                      {suggestion.submitterName && <span>from {suggestion.submitterName}</span>}
                    </Box>
                    {isPersonStory && suggestion.storyTitle && (
                      <Typography variant="subtitle2" fontWeight={900}>
                        {suggestion.storyTitle}
                      </Typography>
                    )}
                    <Typography variant="body2" className="graph-approval-story">
                      {isPersonStory
                        ? suggestion.storyText || suggestion.notes
                        : suggestion.notes || (isPhotoUpdate ? 'Review and approve this replacement photo.' : '')}
                    </Typography>
                    {(suggestion.submitterEmail || suggestion.submitterNote) && (
                      <Typography variant="caption" color="text.secondary">
                        {[suggestion.submitterEmail, suggestion.submitterNote].filter(Boolean).join(' • ')}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} className="graph-approval-actions">
                      <Button
                        variant="contained"
                        onClick={() => approveSuggestion(suggestion)}
                        disabled={isApprovingSuggestion || isRejectingSuggestion}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => rejectSuggestion(suggestion)}
                        disabled={isApprovingSuggestion || isRejectingSuggestion}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
              {!pendingSuggestions.length && (
                <Box className="graph-empty-state">
                  <Typography variant="body2" color="text.secondary">
                    No pending suggestions right now.
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper className={`graph-data-panel${isDataPanelOpen ? ' graph-data-panel--open' : ''}`} elevation={12}>
          <Stack spacing={1.5} className="graph-data-panel-content">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Saved data
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  Browse & bulk edit
                </Typography>
              </Box>
              <IconButton aria-label="Close saved data" onClick={() => setIsDataPanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <TextField
              size="small"
              value={savedDataSearch}
              onChange={(event) => setSavedDataSearch(event.target.value)}
              placeholder="Search saved people or stories"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Tabs
              value={dataPanelTab}
              onChange={(_, value) => setDataPanelTab(value)}
              variant="fullWidth"
              className="graph-data-tabs"
            >
              <Tab value="people" label={`People (${graphData?.people?.length || 0})`} />
              <Tab value="stories" label={`Stories (${graphData?.edges?.length || 0})`} />
            </Tabs>

            <Stack direction="row" spacing={1} className="graph-bulk-actions">
              <Button
                size="small"
                variant="outlined"
                onClick={() => dataPanelTab === 'people' ? selectVisiblePeople(visiblePeople) : selectVisibleEdges(visibleEdges)}
                disabled={dataPanelTab === 'people' ? !visiblePeople.length : !visibleEdges.length}
              >
                Select visible
              </Button>
              <Button size="small" variant="text" onClick={clearSavedSelection} disabled={!bulkDeleteCount}>
                Clear
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={bulkDeleteSavedData}
                disabled={!bulkDeleteCount}
              >
                Delete {bulkDeleteCount || ''}
              </Button>
            </Stack>

            <Divider />

            <Box className="graph-data-list-wrap">
              {dataPanelTab === 'people' ? (
                <List dense disablePadding>
                  {visiblePeople.map((person) => {
                    const connectedCount = (graphData?.edges || []).filter((edge) => edge.person1 === person._id || edge.person2 === person._id).length;
                    return (
                      <ListItemButton key={person._id} className="graph-data-list-item" onClick={() => focusPerson(person)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedSavedPeople.has(person._id)}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleSavedPerson(person._id);
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={person.name}
                          secondary={`${connectedCount} connected ${connectedCount === 1 ? 'story' : 'stories'}${person.funFact ? ` • ${person.funFact}` : ''}`}
                          primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    );
                  })}
                  {!visiblePeople.length && (
                    <Typography variant="body2" color="text.secondary" className="graph-empty-state">
                      No people match this search.
                    </Typography>
                  )}
                </List>
              ) : (
                <List dense disablePadding>
                  {visibleEdges.map((edge) => (
                    <ListItemButton key={edge._id} className="graph-data-list-item" onClick={() => focusEdge(edge)}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedSavedEdges.has(edge._id)}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleSavedEdge(edge._id);
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={getEdgePersonLabel(edge)}
                        secondary={`${originalEdgeIds.has(edge._id) ? 'Map connection' : 'Saved story'} • ${formatDate(edge.dateMet)}${edge.notes ? ` • ${edge.notes}` : ''}`}
                        primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                  {!visibleEdges.length && (
                    <Typography variant="body2" color="text.secondary" className="graph-empty-state">
                      No stories match this search.
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          </Stack>
        </Paper>

        <Paper className={`graph-admin-panel${isGraphPanelOpen ? ' graph-admin-panel--open' : ''}`} elevation={12}>
          <Stack spacing={1.5} className="graph-admin-panel-content">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Map tools
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  Manage map
                </Typography>
              </Box>
              <IconButton aria-label="Close map tools" onClick={() => setIsGraphPanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={800}>
                Name
              </Typography>
              <TextField
                size="small"
                value={graphNameDraft}
                onChange={(event) => setGraphNameDraft(event.target.value)}
                placeholder="Map name"
              />
              <TextField
                size="small"
                label="URL name"
                value={graphUrlNameDraft}
                onChange={(event) => setGraphUrlNameDraft(normalizeGraphUrlName(event.target.value))}
                placeholder="how-you-met-me"
                helperText={`Used for ${getReadGraphPath(graphUrlNameDraft || 'your-map')}`}
              />
              <TextField
                select
                size="small"
                label="Map type"
                value={graphTypeDraft}
                onChange={(event) => setGraphTypeDraft(event.target.value)}
              >
                {GRAPH_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                {GRAPH_TYPE_OPTIONS.find((option) => option.value === graphTypeDraft)?.description}
              </Typography>
              <Stack direction="row" spacing={1} className="graph-admin-actions">
                <Button
                  variant="contained"
                  onClick={saveGraphName}
                  disabled={
                    isRenamingGraph
                    || !graphNameDraft.trim()
                    || !normalizeGraphUrlName(graphUrlNameDraft || graphNameDraft)
                    || (
                      graphNameDraft.trim() === activeGraphName
                      && normalizeGraphUrlName(graphUrlNameDraft) === (graphData?.graph?.slug || '')
                      && graphTypeDraft === activeGraphType
                    )
                  }
                >
                  Save Map
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteCurrentGraph}
                  disabled={isDeletingGraph || !activeGraphName || Boolean(graphData?.graph?.isDefault)}
                >
                  Delete Map
                </Button>
              </Stack>
              {graphData?.graph?.isDefault && (
                <Typography variant="caption" color="text.secondary">
                  The default map cannot be deleted.
                </Typography>
              )}
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={800}>
                Import from another map
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={sourceGraphName}
                  displayEmpty
                  onChange={(event) => selectImportSourceGraph(event.target.value)}
                >
                  <MenuItem value="">
                    Choose source map
                  </MenuItem>
                  {sourceGraphOptions.map((graph) => (
                    <MenuItem key={graph._id || graph.name} value={graph.name}>
                      {graph.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth disabled={!sourceGraphName || isLoadingSourcePeople}>
                <Select
                  value={sourceStartPersonId}
                  displayEmpty
                  onChange={(event) => setSourceStartPersonId(event.target.value)}
                >
                  <MenuItem value="">
                    {isLoadingSourcePeople ? 'Loading people...' : 'Choose start person'}
                  </MenuItem>
                  {sourcePeople.map((person) => (
                    <MenuItem key={person._id} value={person._id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Degrees away"
                value={importDegrees}
                onChange={(event) => setImportDegrees(event.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />
              <Button
                variant="contained"
                onClick={importFromGraph}
                disabled={isImportingGraph || !sourceGraphName || !sourceStartPersonId}
              >
                {isImportingGraph ? 'Importing...' : 'Import Neighborhood'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper className={`graph-insights-panel${isInsightsPanelOpen ? ' graph-insights-panel--open' : ''}`} elevation={12}>
          <Stack spacing={1.5} className="graph-insights-panel-content">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Discovery & insights
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  Relationship analytics
                </Typography>
              </Box>
              <IconButton aria-label="Close insights" onClick={() => setIsInsightsPanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Box className="graph-insight-section">
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} className="graph-insight-section-header">
                <Typography variant="subtitle2" fontWeight={800}>
                  Relationship History
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={isTimelinePlaying || isTimelineActive ? <StopIcon /> : <PlayArrowIcon />}
                  onClick={isTimelinePlaying || isTimelineActive ? stopTimeline : startTimeline}
                  disabled={!timelineEdges.length}
                >
                  {isTimelinePlaying || isTimelineActive ? 'Stop' : 'Play'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {isTimelineActive
                  ? `${timelineEdgeCount}/${timelineEdges.length} friendships formed${currentTimelineEdge ? ` • ${getFriendshipLabel(currentTimelineEdge)}` : ''}`
                  : `${timelineEdges.length} dated friendships in this map.`}
              </Typography>
              <List dense disablePadding className="graph-history-list">
                {timelineEdges.slice(0, 8).map((edge) => (
                  <ListItemText
                    key={edge._id}
                    primary={getFriendshipLabel(edge)}
                    secondary={edge.notes}
                    primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                ))}
              </List>
            </Box>

            <Divider />

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                How am I connected?
              </Typography>
              <Stack direction="row" spacing={1} className="graph-insight-controls">
                <FormControl size="small" fullWidth>
                  <Select value={pathStartPersonId} onChange={(event) => setPathStartPersonId(event.target.value)}>
                    {analyticsPeople.map((person) => (
                      <MenuItem key={person._id} value={person._id}>{person.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <Select value={pathEndPersonId} onChange={(event) => setPathEndPersonId(event.target.value)}>
                    {analyticsPeople.map((person) => (
                      <MenuItem key={person._id} value={person._id}>{person.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="body2" className="graph-path-result">
                {pathPersonIds.length
                  ? `${pathPersonIds.length - 1} degrees: ${pathPersonIds.map(getPersonName).join(' -> ')}`
                  : 'No connection path found.'}
              </Typography>
            </Box>

            <Divider />

            <Box className="graph-insight-grid">
              <Box>
                <Typography variant="overline" color="text.secondary">Most influential connector</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {graphAnalytics.mostInfluential ? `${getPersonName(graphAnalytics.mostInfluential.personId)} introduced ${graphAnalytics.mostInfluential.count}` : 'Not enough history yet'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Most central person</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {graphAnalytics.mostCentral ? getPersonName(graphAnalytics.mostCentral.personId) : 'Not enough connections yet'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Average distance</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {graphAnalytics.averageDistance ? graphAnalytics.averageDistance.toFixed(2) : '0'} degrees
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Largest recruitment chain</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {graphAnalytics.largestRecruitmentChain?.count ? `${getPersonName(graphAnalytics.largestRecruitmentChain.personId)} • ${graphAnalytics.largestRecruitmentChain.count}` : 'No chain yet'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Oldest friendship</Typography>
                <Typography variant="body2" fontWeight={700}>{getFriendshipLabel(graphAnalytics.oldestFriendship)}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Most recent friendship</Typography>
                <Typography variant="body2" fontWeight={700}>{getFriendshipLabel(graphAnalytics.mostRecentFriendship)}</Typography>
              </Box>
            </Box>

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                Degrees of separation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {graphAnalytics.degreeDistribution.length
                  ? graphAnalytics.degreeDistribution.map(([degree, count]) => `${degree}: ${count}`).join(' • ')
                  : 'No connected pairs yet.'}
              </Typography>
            </Box>

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                People who introduced the most others
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {graphAnalytics.topIntroducers.length
                  ? graphAnalytics.topIntroducers.slice(0, 5).map((item) => `${getPersonName(item.personId)} (${item.count})`).join(' • ')
                  : 'No introduction pattern yet.'}
              </Typography>
            </Box>

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                Small world connections
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {smallWorldEdges.length
                  ? smallWorldEdges.slice(0, 6).map((edge) => `${getEdgePersonLabel(edge)} (${formatDate(edge.dateMet)})`).join(' • ')
                  : 'No extra random connections outside the original chain yet.'}
              </Typography>
            </Box>

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                Hidden connectors
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {graphAnalytics.hiddenConnectors.length
                  ? graphAnalytics.hiddenConnectors.slice(0, 5).map((item) => getPersonName(item.personId)).join(' • ')
                  : 'No quiet bridge people detected yet.'}
              </Typography>
            </Box>

            <Box className="graph-insight-section">
              <Typography variant="subtitle2" fontWeight={800}>
                What if this person never joined?
              </Typography>
              <FormControl size="small" fullWidth>
                <Select value={whatIfPersonId} onChange={(event) => setWhatIfPersonId(event.target.value)}>
                  {analyticsPeople.map((person) => (
                    <MenuItem key={person._id} value={person._id}>{person.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {whatIfResult
                  ? `Without ${getPersonName(whatIfPersonId)}, the map becomes ${whatIfResult.componentCount} group${whatIfResult.componentCount === 1 ? '' : 's'}; largest group has ${whatIfResult.largestComponentSize} people.`
                  : 'Choose a person to simulate impact.'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {error && (
          <Paper className="graph-error-banner" elevation={8}>
            <Typography variant="body2">{error.message}</Typography>
          </Paper>
        )}
        {layoutMessage && (
          <Paper className="graph-layout-banner" elevation={8}>
            <Typography variant="body2">{layoutMessage}</Typography>
            <IconButton size="small" aria-label="Dismiss layout message" onClick={() => setLayoutMessage('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        )}

        <CreatePersonModal
          isModalOpen={isPanelOpen}
          setIsModalOpen={setIsPanelOpen}
          refetchGraph={refetchGraph}
          people={graphData?.people}
          edges={graphData?.edges || []}
          graphName={graphName}
          graphType={activeGraphType}
        />
        <CreateExistingEdgeModal
          isModalOpen={isCreateEdgeOpen}
          setIsModalOpen={setIsCreateEdgeOpen}
          refetchGraph={refetchGraph}
          people={graphData?.people || []}
          graphName={graphName}
          graphType={activeGraphType}
        />
        <EditPersonModal
          selectedNode={selectedNode}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          refetchGraph={refetchGraph}
          headerBackgroundColor={selectedNode && getColor(selectedNode?.id)}
          graphType={activeGraphType}
        />
        <EditEdgeModal
          selectedEdge={selectedEdge}
          isModalOpen={isEdgeModalOpen}
          setIsModalOpen={setIsEdgeModalOpen}
          refetchGraph={refetchGraph}
          person1={selectedEdgePerson1}
          person2={selectedEdgePerson2}
          graphType={activeGraphType}
        />
        <ForceGraph2D
          ref={graphRef}
          graphData={displayedData}
          warmupTicks={80}
          backgroundColor="#050505"
          nodeLabel={(node) => node.name}
          onNodeClick={(node) => handleNodeClick(node)}
          onLinkClick={(link) => handleEdgeClick(link)}
          onBackgroundClick={() => {
            setSelectedNode(null);
            setSelectedEdge(null);
          }}
          cooldownTicks={80}
          onEngineStop={() => {
            applyGraphPhysics(false);
            zoomToFit();
          }}
            nodeCanvasObject={(node, ctx, globalScale) => {
            const isSelected = selectedNode?.id === node.id || selectedNode?._id === node._id;
            const size = Math.max(20, GRAPH_NODE_SIZE / Math.sqrt(Math.max(globalScale, 0.85)));
            ctx.beginPath();
            ctx.arc(node.x, node.y, size / 2, 0, 2 * Math.PI);
            ctx.fillStyle = getColor(node.id);
            ctx.fill();
            ctx.closePath();

            if (isSelected) {
              ctx.beginPath();
              ctx.arc(node.x, node.y, size / 2 + 5 / globalScale, 0, 2 * Math.PI);
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 2.4 / globalScale;
              ctx.stroke();
              ctx.closePath();
            }

            const imgSize = size - 2;
            const img = getPersonPhotoImage(node, {
              globalScale,
              onLoad: scheduleGraphRefresh,
            });
            drawNodePhoto(ctx, node, img, imgSize, getColor(node.id));
          }}
          linkLabel={(link) => link.note?.length > 20 ? link.note.slice(0, 20) + '...' : link.note}
          linkDirectionalArrowLength={0}
          linkCanvasObject={(link, ctx, globalScale) => {
                const isSelected = selectedEdge?.id === link.id || selectedEdge?._id === link._id;
                ctx.beginPath();
                ctx.save();
                ctx.strokeStyle = isSelected ? '#ffffff' : getEdgeCategoryColor(link.category);
                ctx.lineWidth = isSelected ? 3 / globalScale : 1.7 / globalScale;
                ctx.moveTo(link.source.x, link.source.y);
                ctx.lineTo(link.target.x, link.target.y);
                ctx.stroke();
                ctx.restore();
          }}
        />

        <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={3000} // Adjust as needed
        onClose={() => setIsSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        backgroundColor={theme.palette.info.main}
      >
        <SnackbarContent
          sx={{ backgroundColor: theme.palette.info.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          message={
            <Box sx={{ display: 'flex', width:"100%", justifyContent: 'space-between' }}>

             <Button
                variant='contained'
                color="primary"
                size="small"
                onClick={() => {
                  // Handle button click here
                    setIsEdgeModalOpen(true);
                    setIsSnackbarOpen(false);
                }}
              >
                View Story
              </Button>
            
              <Button
                variant='contained'
                color="primary"
                size="small"
                onClick={() => {
                  // Handle button click here
                    setIsEdgeModalOpen(true);
                    setIsSnackbarOpen(false);
                }}
              >
                Edit Edge
              </Button>
            
            </Box>
          }
        />
      </Snackbar>
      </div>
    </div>
  );
}
